import styles from './InvoiceCard.module.css';

import type { FC } from 'react';
import type { DealChain } from 'src/utils/dealChain';

import React from 'react';
import classNames from 'classnames';

import { formatDateTime } from 'src/utils/date';
import { dealTxLink, shortenHash } from 'src/utils/dealChain';

import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';

/**
 * One invoice, in one format, everywhere (contracts v3, UI part §4).
 *
 * Both verticals and all three cabinets show the buyer the same card, because an invoice that is
 * laid out differently on the screen where you paid and the screen where you check what you paid
 * is an invoice people photograph and ask support about. Label–value lines, a total set apart, and
 * below it the order it belongs to.
 *
 * Two fields are mandatory and were the actual bug report behind this component: **the date** —
 * the moment the payment happened, or an explicit "Pending" before it — and **the link to the
 * payment transaction**. A receipt with neither is a price list.
 *
 * A second currency (the legacy TRON gas compensation) may appear only as a footnote below the
 * total, never as a line inside it: two currencies do not add up, and a total that pretends they
 * do is wrong by exactly the smaller number.
 */

export interface InvoiceLine {
  label: string;
  value: string;
}

interface InvoiceCardProps {
  title?: string;
  lines: InvoiceLine[];
  total: InvoiceLine;
  /** Shown under the total, in its own currency, outside the sum. */
  footnote?: InvoiceLine | null;
  /**
   * A sentence under the total about what the total does not include — the network fees the
   * buyer's own wallet pays on Base (api#380, §2.9). Prose, not a line: it has no amount, and an
   * amount nobody can promise must not look like one.
   */
  note?: string | null;
  /** When the money moved. `null` renders `issuedFallback` — the invoice is not yet a receipt. */
  issuedAt?: string | null;
  /**
   * What to print instead of a date. "Pending" by default, because usually a missing date means
   * the payment has not happened. It is not always so: a rental on a chain whose poller records no
   * events is paid without a recorded moment, and there the honest word is "Paid".
   */
  issuedFallback?: string;
  /** The transaction that carried the payment. */
  paymentTxHash?: string | null;
  chain?: DealChain | null;
  /** Order / Recipient / Phone / City — the shipment half of the card. */
  meta?: InvoiceLine[];
}

export const InvoiceCard: FC<InvoiceCardProps> = ({
  title = 'Invoice',
  lines,
  total,
  footnote,
  note,
  issuedAt,
  issuedFallback = 'Pending',
  paymentTxHash,
  chain,
  meta,
}) => {
  const txLink = dealTxLink(chain ?? null, paymentTxHash);

  return (
    <Stack gap={1} className={styles.card}>
      <Row>
        <Typography color="white" fontWeight="600">
          {title}
        </Typography>
        <Typography color="lightWhite" variant="small">
          {issuedAt ? formatDateTime(issuedAt) : issuedFallback}
        </Typography>
      </Row>

      {lines.map((line) => (
        <Row key={line.label}>
          <Typography color="lightWhite" variant="small">
            {line.label}
          </Typography>
          <Typography color="white" variant="small">
            {line.value}
          </Typography>
        </Row>
      ))}

      <div className={classNames(styles.row, styles.total)}>
        <Typography color="white" variant="small" fontWeight="600">
          {total.label}
        </Typography>
        <Typography color="white" variant="small" fontWeight="600">
          {total.value}
        </Typography>
      </div>

      {footnote ? (
        <div className={classNames(styles.row, styles.footnote)}>
          <Typography color="lightWhite" variant="small">
            {footnote.label}
          </Typography>
          <Typography color="lightWhite" variant="small">
            {footnote.value}
          </Typography>
        </div>
      ) : null}

      {note ? (
        <Typography color="lightWhite" variant="small" className={styles.note}>
          {note}
        </Typography>
      ) : null}

      {paymentTxHash ? (
        <Row>
          <Typography color="lightWhite" variant="small">
            Payment transaction
          </Typography>
          <Typography color="lightWhite" variant="small">
            {txLink ? (
              <a className={styles.link} href={txLink} target="_blank" rel="noreferrer">
                {shortenHash(paymentTxHash)}
              </a>
            ) : (
              shortenHash(paymentTxHash)
            )}
          </Typography>
        </Row>
      ) : null}

      {meta?.length ? (
        <Stack gap={1} className={styles.meta}>
          {meta.map((line) => (
            <Row key={line.label}>
              <Typography color="lightWhite" variant="small">
                {line.label}
              </Typography>
              <Typography color="white" variant="small">
                {line.value}
              </Typography>
            </Row>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
};

const Row: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles.row}>{children}</div>
);
